import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Map, Eye, Filter, Search, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMapStore } from '../../../store/mapStore';
import EnhancedLotsTable from './EnhancedLotsTable';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedLots.css';
import ExpandableText from '../../../components/common/ExpandableText';
import { supabase } from '../../../lib/supabase';
import { formatTextWithLinks } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';
import ViewLotModal from './components/ViewLotModal';
import EditLotModal from '../../../pages/Admin/Lots/components/EditLotModal';

import LotFilters from '../../../components/Lots/LotFilters';
import type { LotFilter, LotSortConfig } from '../../../types/lot';

// Define the Lot type
interface Lot {
  lot_id: string;
  community_name: string | null;
  lot_perm_use: string | null;
  lot_zone: string | null;
  zone_desc?: string | null;
  lot_desc: string | null;
  leaselot_lease: number | null;
  leaselot_lot: string | null;
  leaselot_use: string | null;
  holder_name: string | null;
  lot_avail?: string | null; // Added for table column/filter
  lot_surr?: string | null;  // Added for table column/filter
  lot_surr_note?: string | null; // Added for table column
}


// Define sort config type
interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

export default function EnhancedLots() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, canManageSystemSettings } = usePermissions();
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<LotFilter>({
    community: null,
    leaseId: null,
    zone: null,
    leaseholder: null,
    lotAvail: null, // Initialize new filter
    lotSurr: null   // Initialize new filter
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'community_name',
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
// State for modals
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLot, setSelectedLot] = useState<any | null>(null);
  const [selectedEditLot, setSelectedEditLot] = useState<any | null>(null);

  // Fetch lots data
  const { data: lots, isLoading, error } = useQuery<Lot[]>({
    queryKey: ['lots'],
    queryFn: async () => {
      try {
        // First get the basic land_lots data
        const { data: landLotsData, error: landLotsError } = await supabase
          .from('land_lots')
          .select(`
            lot_id, 
            lot_community, 
            lot_perm_use, 
            lot_zone, 
            lot_desc, 
            lot_avail,
            lot_surr,
            lot_surr_note,
            zones!land_lots_lot_zone_fkey (zone_desc)
          `);

        if (landLotsError) {
          console.error('Error fetching land_lots:', landLotsError);
          throw landLotsError;
        }

        // Get the communities data to map community IDs to names
        const { data: communitiesData, error: communitiesError } = await supabase
          .from('communities')
          .select('comm_id, comm_name');

        if (communitiesError) {
          console.error('Error fetching communities:', communitiesError);
          throw communitiesError;
        }

        // Create a mapping of community IDs to names
        const communityMap = communitiesData.reduce((map: Record<string, string>, community: { comm_id: string, comm_name: string }) => {
          map[community.comm_id] = community.comm_name;
          return map;
        }, {});

        // Get the lease_lots data to join with land_lots
        const { data: leaseLotsData, error: leaseLotsError } = await supabase
          .from('lease_lots')
          .select('leaselot_lease, leaselot_lot, leaselot_use');

        if (leaseLotsError) {
          console.error('Error fetching lease_lots:', leaseLotsError);
          throw leaseLotsError;
        }

        // Create a mapping of lot IDs to lease_lots data
        const leaseLotsMap = leaseLotsData.reduce((map: Record<string, any>, leaseLot: { leaselot_lease: number, leaselot_lot: string, leaselot_use: string }) => {
          map[leaseLot.leaselot_lot] = leaseLot;
          return map;
        }, {});

        // Get the leaseholders data directly from lot_lease_summary by lot_id
        const lotIds = landLotsData.map((lot: any) => lot.lot_id);

        const { data: leaseholdersData, error: leaseholdersError } = await supabase
          .from('lot_lease_summary')
          .select('lot_id, holder_name')
          .in('lot_id', lotIds);

        if (leaseholdersError) {
          console.error('Error fetching leaseholders:', leaseholdersError);
          throw leaseholdersError;
        }

        // Create a mapping of lot IDs to holder names
        const lotToHolderMap = leaseholdersData.reduce((map: Record<string, string>, item: { lot_id: string, holder_name: string }) => {
          if (item.holder_name) {
            map[item.lot_id] = item.holder_name;
          }
          return map;
        }, {});

        // Combine the data
        const combinedData = landLotsData.map((lot: any) => {
          const leaseLotData = leaseLotsMap[lot.lot_id] || {};
          const holderName = lotToHolderMap[lot.lot_id] || null;
          
          // Log the raw data to debug
          console.log('Raw lot data for ' + lot.lot_id + ':', {
            lot_avail: lot.lot_avail,
            lot_surr: lot.lot_surr,
            lot_surr_note: lot.lot_surr_note
          });
          
          return {
            lot_id: lot.lot_id,
            community_name: communityMap[lot.lot_community] || null,
            lot_perm_use: lot.lot_perm_use,
            lot_zone: lot.lot_zone,
            zone_desc: lot.zones ? lot.zones.zone_desc : null,
            lot_desc: lot.lot_desc,
            lot_avail: lot.lot_avail || null,  // Ensure we explicitly handle undefined/falsy values
            lot_surr: lot.lot_surr || null,    // Ensure we explicitly handle undefined/falsy values 
            lot_surr_note: lot.lot_surr_note || null,
            leaselot_lease: leaseLotData.leaselot_lease || null,
            leaselot_lot: leaseLotData.leaselot_lot || null,
            leaselot_use: leaseLotData.leaselot_use || null,
            holder_name: holderName
          };
        });

        return combinedData;
      } catch (error) {
        console.error('Error in fetchLots:', error);
        throw error;
      }
    }
  });

  // Extract unique communities, zones, lease IDs, and leaseholders for filter dropdowns
  const { communities, zones, leaseIds, leaseholders, zoneDescriptions } = useMemo(() => {
    if (!lots) return { communities: [], zones: [], leaseIds: [], leaseholders: [], zoneDescriptions: {} };
    
    const communitiesSet = new Set<string>();
    const zonesSet = new Set<string>();
    const leaseIdsSet = new Set<string>();
    const leaseholdersSet = new Set<string>();
    const zoneDescMap: Record<string, string> = {};
    
    lots.forEach(lot => {
      if (lot.community_name) communitiesSet.add(lot.community_name);
      if (lot.lot_zone) {
        zonesSet.add(lot.lot_zone);
        if (lot.zone_desc) {
          zoneDescMap[lot.lot_zone] = lot.zone_desc;
        }
      }
      if (lot.leaselot_lease) leaseIdsSet.add(String(lot.leaselot_lease));
      if (lot.holder_name) leaseholdersSet.add(lot.holder_name);
    });
    
    return {
      communities: Array.from(communitiesSet).sort(),
      zones: Array.from(zonesSet).sort(),
      leaseIds: Array.from(leaseIdsSet).sort((a, b) => Number(a) - Number(b)),
      leaseholders: Array.from(leaseholdersSet).sort(),
      zoneDescriptions: zoneDescMap
    };
  }, [lots]);

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<LotFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle view on map
  const handleViewOnMap = (lot: Lot) => {
    // Set the selected lot ID in the map store
    useMapStore.getState().setSelectedLease(lot.lot_id);
    
    // Navigate to the map view with the selected lot ID
    navigate('/map', { 
      state: { 
        fromLeases: true, 
        selectedLease: lot.lot_id 
      }
    });
  };

  // Handle view lot details
  const handleViewLot = (lot: Lot) => {
    setSelectedLot(lot);
    setIsViewModalOpen(true);
  };

  // Handle edit lot
  const handleEditLot = async (lot: Lot) => {
    try {
      // Fetch the full lot data from the database before opening the edit modal
      const { data, error } = await supabase
        .from('land_lots')
        .select('*')
        .eq('lot_id', lot.lot_id)
        .single();
      
      if (error) {
        console.error('Error fetching lot details:', error);
        return;
      }
      
      // Set the data for the edit modal
      setSelectedEditLot({
        ...data,
        community_name: lot.community_name, // Keep the joined data
        holder_name: lot.holder_name,
        leaselot_lease: lot.leaselot_lease,
        leaselot_lot: lot.leaselot_lot,
        leaselot_use: lot.leaselot_use
      });
      setIsEditModalOpen(true);
    } catch (err) {
      console.error('Error in handleEditLot:', err);
    }
  };

  // Apply filters and sorting
  const filteredAndSortedLots = useMemo(() => {
    if (!lots) return [];
    
    // Apply filters
    let filtered = lots.filter(lot => {
      // Apply community filter
      if (filters.community && lot.community_name !== filters.community) {
        return false;
      }
      
      // Apply lease ID filter
      if (filters.leaseId && (!lot.leaselot_lease || !String(lot.leaselot_lease).includes(filters.leaseId))) {
        return false;
      }
      
      // Apply zone filter
      if (filters.zone && lot.lot_zone !== filters.zone) {
        return false;
      }
      
      // Apply leaseholder filter
      if (filters.leaseholder && lot.holder_name !== filters.leaseholder) {
        return false;
      }
      
      // Apply Lot Available filter
      if (filters.lotAvail && lot.lot_avail !== filters.lotAvail) {
        return false;
      }
      
      // Apply Lot Surrendered filter
      if (filters.lotSurr && lot.lot_surr !== filters.lotSurr) {
        return false;
      }
      
      // Apply search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          (lot.lot_id && lot.lot_id.toLowerCase().includes(query)) ||
          (lot.community_name && lot.community_name.toLowerCase().includes(query)) ||
          (lot.lot_perm_use && lot.lot_perm_use.toLowerCase().includes(query)) ||
          (lot.lot_zone && lot.lot_zone.toLowerCase().includes(query)) ||
          (lot.lot_desc && lot.lot_desc.toLowerCase().includes(query)) ||
          (lot.leaselot_use && lot.leaselot_use.toLowerCase().includes(query))
        );
      }
      
      return true;
    });
    
    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.field as keyof Lot];
      const bValue = b[sortConfig.field as keyof Lot];
      
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }
      
      return 0;
    });
    
    return filtered;
  }, [lots, filters, sortConfig, searchQuery]);

  // Apply pagination
  const paginatedLots = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedLots.slice(startIndex, endIndex);
  }, [filteredAndSortedLots, currentPage, pageSize]);

  // Define table columns
  const columns: TableColumn<Lot>[] = [
    { 
      id: 'edit', 
      header: 'EDIT', 
      accessor: (row: Lot) => (
        <button
          onClick={() => handleEditLot(row)}
          className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-primary bg-gray-50 hover:bg-gray-100 rounded-lg"
          title="Edit Lot"
        >
          <Pencil className="h-6 w-6" />
        </button>
      ),
      width: 'narrow' as ColumnWidth,
      align: 'center',
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    { 
      id: 'map', 
      header: 'Map', 
      accessor: (row: Lot) => (
        <button
          onClick={() => handleViewOnMap(row)}
          className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-primary bg-gray-50 hover:bg-gray-100 rounded-lg"
          title="View on Map"
        >
          <Map className="h-6 w-6" />
        </button>
      ),
      width: 'narrow' as ColumnWidth,
      align: 'center'
    },
    { 
      id: 'view', 
      header: 'View', 
      accessor: (row: Lot) => (
        <button
          onClick={() => handleViewLot(row)}
          className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-primary bg-gray-50 hover:bg-gray-100 rounded-lg"
          title="View Details"
        >
          <Eye className="h-6 w-6" />
        </button>
      ),
      width: 'narrow' as ColumnWidth,
      align: 'center'
    },
    { 
      id: 'lot_id', 
      header: 'Lot ID', 
      accessor: (row: Lot) => row.lot_id,
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'community_name', 
      header: 'Community', 
      accessor: (row: Lot) => row.community_name || '-',
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'holder_name', 
      header: 'Leaseholder', 
      accessor: (row: Lot) => (
        <div className="truncate" title={row.holder_name || '-'}>
          {formatTextWithLinks(row.holder_name || '-')}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    {
      id: 'lot_avail',
      header: 'Available',
      accessor: (row: Lot) => row.lot_avail === 'Y' ? 'Yes' : 'No',
      width: 'medium' as ColumnWidth,
      align: 'center',
      sortable: true
    },
    { 
      id: 'lot_perm_use', 
      header: 'Permitted Use', 
      accessor: (row: Lot) => (
        <div className="truncate" title={row.lot_perm_use || 'Not specified'}>
          {formatTextWithLinks(row.lot_perm_use || 'Not specified')}
        </div>
      ),
      width: 'wide' as ColumnWidth
    },
    { 
      id: 'lot_zone', 
      header: 'Zone', 
      accessor: (row: Lot) => row.lot_zone || '-',
      width: 'narrow' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'lot_desc', 
      header: 'Description', 
      accessor: (row: Lot) => (
        <ExpandableText 
          text={row.lot_desc || 'Not specified'} 
          maxLength={50} 
          className="text-sm"
        />
      ),
      width: 'wide' as ColumnWidth
    },
    {
      id: 'lot_surr',
      header: 'Surrendered',
      accessor: (row: Lot) => row.lot_surr === 'Y' ? 'Yes' : 'No',
      width: 'narrow' as ColumnWidth,
      align: 'center',
      sortable: true
    },
    {
      id: 'lot_surr_note',
      header: 'Surrender Notes',
      accessor: (row: Lot) => (
        <ExpandableText 
          text={row.lot_surr_note || 'Not specified'} 
          maxLength={50} 
          className="text-sm"
        />
      ),
      width: 'wide' as ColumnWidth
    },
    { 
      id: 'leaselot_lease', 
      header: 'Lease ID', 
      accessor: (row: Lot) => row.leaselot_lease || '-',
      width: 'narrow' as ColumnWidth,
      sortable: true
    }
  ];

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: filteredAndSortedLots.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading lots data. Please try again later.
      </div>
    );
  }

  return (
    <div className="enhanced-lots-container p-4">
      <div className="enhanced-lots-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Lots</h1>
          </div>

          <div className="flex items-center gap-2">
            <LotFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              isFilterOpen={isFilterOpen}
              onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
              filters={filters}
              onFilterChange={handleFilterChange}
              communities={communities}
              zones={zones}
              leaseIds={leaseIds}
              leaseholders={leaseholders}
              zoneDescriptions={zoneDescriptions}
              showExport={false}
            />
            <div className="text-base font-bold text-gray-700">
              Total Lots: {filteredAndSortedLots.length}
            </div>
          </div>
        </div>
      </div>
      
      <div className="enhanced-lots-table-container enhanced-table-force-scroll">
        <EnhancedLotsTable
          columns={columns}
          data={paginatedLots}
          keyField="lot_id"
          width="2800px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading}
          emptyMessage="No lots found"
          horizontalScroll={true}
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={(newSortConfig) => {
            console.log('Sorting:', newSortConfig);
            setSortConfig(newSortConfig);
          }}
          permissions={{
            isAdmin,
            canManageSystemSettings
          }}
        />
      </div>
      
      {/* View modal */}
      {selectedLot && (
        <ViewLotModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedLot(null);
          }}
          lot={selectedLot}
        />
      )}

      {/* Edit modal */}
      {selectedEditLot && (
        <EditLotModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedEditLot(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['lots'] });
            setIsEditModalOpen(false);
            setSelectedEditLot(null);
          }}
          lot={selectedEditLot}
        />
      )}
    </div>
  );
}
